import { useEffect, useRef, useState } from "react";
import { Download, FileText, Loader2, X } from "lucide-react";
import { generateReport } from "../lib/enterprise";

function BlobThumb({ blob }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!blob) return;
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  if (!url) return <div className="rb-thumb-placeholder" />;
  return <img src={url} alt="" className="rb-thumb" />;
}

export default function ReportBuilder({ photos, onClose }) {
  const [title,    setTitle]    = useState("Field Report");
  const [subtitle, setSubtitle] = useState("");
  const [company,  setCompany]  = useState("");
  const [notes,    setNotes]    = useState({});
  const [building, setBuilding] = useState(false);
  const [done,     setDone]     = useState(false);

  const handleNote = (id, val) => setNotes((prev) => ({ ...prev, [id]: val }));

  const handleBuild = async () => {
    setBuilding(true);
    try {
      const pdf = await generateReport({
        title, subtitle, company,
        date: new Date().toLocaleDateString(),
        photos,
        notes,
      });
      const filename = `${(title || "report").replace(/\W+/g, "-").toLowerCase()}-${Date.now()}.pdf`;
      pdf.save(filename);
      setDone(true);
    } catch (err) {
      console.error("Report build failed:", err);
      alert("Report generation failed. Please try again.");
    } finally {
      setBuilding(false);
    }
  };

  return (
    <div className="rb-overlay" role="dialog" aria-modal="true">
      <div className="rb-panel">
        {/* Header */}
        <header className="rb-header">
          <div className="rb-header-left">
            <FileText size={18} className="rb-header-icon" />
            <span className="rb-header-title">Report Builder</span>
            <span className="rb-header-count">{photos.length} photo{photos.length !== 1 ? "s" : ""}</span>
          </div>
          <button className="rb-close" onClick={onClose}><X size={18} /></button>
        </header>

        <div className="rb-body">
          {/* Report metadata */}
          <section className="rb-section">
            <h3 className="rb-section-title">Report Details</h3>
            <div className="rb-field-grid">
              <label className="rb-field">
                <span>Title *</span>
                <input
                  className="rb-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Field Report"
                  maxLength={80}
                />
              </label>
              <label className="rb-field">
                <span>Subtitle</span>
                <input
                  className="rb-input"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder="Site inspection, audit results…"
                  maxLength={100}
                />
              </label>
              <label className="rb-field">
                <span>Company / Author</span>
                <input
                  className="rb-input"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Your company or name"
                  maxLength={60}
                />
              </label>
            </div>
          </section>

          {/* Photo list with per-photo notes */}
          <section className="rb-section">
            <h3 className="rb-section-title">Photos & Notes</h3>
            <div className="rb-photo-list">
              {photos.map((item, i) => (
                <div key={item.id} className="rb-photo-row">
                  <div className="rb-photo-info">
                    <span className="rb-photo-num">{i + 1}</span>
                    <BlobThumb blob={item.editedBlob || item.blob} />
                    <div className="rb-photo-meta">
                      <span className="rb-photo-name">{item.name || item.title || "Untitled"}</span>
                      <span className="rb-photo-date">
                        {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
                        {item.projectName ? ` · ${item.projectName}` : ""}
                        {item.aiCategory && item.aiCategory !== "other" ? ` · ${item.aiCategory}` : ""}
                      </span>
                    </div>
                  </div>
                  <textarea
                    className="rb-note-input"
                    value={notes[item.id] || ""}
                    onChange={(e) => handleNote(item.id, e.target.value)}
                    placeholder="Add caption or note for this photo…"
                    rows={2}
                    maxLength={400}
                  />
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer className="rb-footer">
          {done && (
            <span className="rb-done-msg">✅ PDF downloaded successfully</span>
          )}
          <button className="rb-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="rb-build-btn"
            onClick={handleBuild}
            disabled={building || !title.trim()}
          >
            {building
              ? <><Loader2 size={16} className="spin" /> Generating…</>
              : <><Download size={16} /> Export PDF</>}
          </button>
        </footer>
      </div>
    </div>
  );
}
